import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ScrollArea } from '../../ui/scroll-area';
import { Badge } from '../../ui/badge';
import { Sparkles, GripHorizontal, Send, BarChart3, ChevronDown, ChevronUp, X } from 'lucide-react';
import * as ss from 'simple-statistics';

// @ts-ignore
import jStat from 'jstat';
import { useUserSettings } from '../../../hooks/useUserSettings';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { sendMessageToModel } from '@/services/ModelService';
import type { AIModel, Message } from '@/types';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

type WorkspaceTab = 'solutions' | 'data';

type AssistantModuleId =
  | 'home'
  | 'graph-builder'
  | 'descriptive'
  | 'hypothesis'
  | 'regression'
  | 'distributions'
  | 'visualization'
  | 'capability-control'
  | 'doe-optimizer'
  | 'time-series'
  | 'measurement-systems';

type AssistantIntent =
  | 'descriptive'
  | 'hypothesis'
  | 'regression'
  | 'distribution'
  | 'graph'
  | 'capability'
  | 'forecast'
  | 'msa'
  | 'doe';

type LocalMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
};

type PlanSlots = {
  subtype?: string;
  columnId?: string;
  secondColumnId?: string;
  columnIds?: string[];
  dependentColumnId?: string;
  independentColumnId?: string;
  distributionType?: string;
  graphType?: string;
  alpha?: number;
  nullValue?: number;
  tail?: 'two-tailed' | 'greater' | 'less';
  lsl?: number;
  usl?: number;
  method?: 'moving-average' | 'exponential-smoothing' | 'trend';
  horizon?: number;
  windowSize?: number;
  smoothingAlpha?: number;
  studyType?: 'type1' | 'crossed';
  referenceValue?: number;
  measurementColumnId?: string;
  partColumnId?: string;
  operatorColumnId?: string;
  factorCount?: 2 | 3;
  responseColumnId?: string;
  goal?: 'maximize' | 'minimize' | 'target';
  targetValue?: number;
  xValue?: number;
  parameterA?: number;
  parameterB?: number;
};

type AssistantPlan = {
  intent: AssistantIntent;
  slots: PlanSlots;
  originalPrompt: string;
};

type PendingQuestion = {
  prompt: string;
  field: keyof PlanSlots | 'factorColumns';
  kind:
    | 'numeric-column'
    | 'any-column'
    | 'numeric-columns'
    | 'choice'
    | 'number';
  count?: number;
  options?: Array<{ label: string; value: string }>;
  excludeIds?: string[];
};

type AssistantRoute = {
  moduleId: AssistantModuleId;
  selectedColumnId?: string;
  workspaceTab?: WorkspaceTab;
};

type MapleStatsAiAssistantProps = {
  worksheet: ParsedWorksheet;
  onRoute: (route: AssistantRoute) => void;
};

const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const formatNumber = (value: number, digits = 4) => (Number.isFinite(value) ? value.toFixed(digits) : '—');
const unique = <T,>(items: T[]) => Array.from(new Set(items));
const FRONTIER_PROVIDERS = new Set<AIModel['provider']>(['openai', 'gemini', 'deepseek', 'cohere', 'maplept']);

const isFrontierModel = (model: AIModel | null) => Boolean(model && FRONTIER_PROVIDERS.has(model.provider));

const d2Table: Record<number, number> = {
  2: 1.128,
  3: 1.693,
  4: 2.059,
  5: 2.326,
};

const detectIntent = (prompt: string): AssistantIntent | null => {
  const text = normalize(prompt);

  if (/(t test|ttest|z test|ztest|hypothesis|p value|significance test)/.test(text)) return 'hypothesis';
  if (/(regression|fit line|predict y|linear model|polynomial)/.test(text)) return 'regression';
  if (/(descriptive|summary stats|summarize data|mean median|describe this data)/.test(text)) return 'descriptive';
  if (/(distribution|normal|poisson|binomial|chi square|cdf|probability)/.test(text)) return 'distribution';
  if (/(histogram|scatter|bubble|pareto|heatmap|boxplot|run chart|chart|graph|plot)/.test(text)) return 'graph';
  if (/(capability|cpk|control chart|spc|ppm out)/.test(text)) return 'capability';
  if (/(forecast|time series|moving average|smoothing|trend forecast)/.test(text)) return 'forecast';
  if (/(msa|measurement system|gage|grr|type 1 study|crossed study)/.test(text)) return 'msa';
  if (/(doe|design of experiments|factorial|response optimizer|optimiz)/.test(text)) return 'doe';
  return null;
};

const buildWorksheetContext = (worksheet: ParsedWorksheet) => {
  const numeric = worksheet.numericColumns.map((column, index) => `${index + 1}. ${column.name}`).join(', ');
  const all = worksheet.columns.map((column, index) => `${index + 1}. ${column.name}`).join(', ');
  return {
    hasData: worksheet.rowCount > 0,
    summary: worksheet.rowCount > 0
      ? `Worksheet loaded: ${worksheet.rowCount} rows, ${worksheet.columnCount} columns, ${worksheet.numericColumns.length} numeric columns.`
      : 'Worksheet is empty. Open, import, or paste data in the Data tab first.',
    numericList: numeric || 'No numeric columns yet.',
    allList: all || 'No columns yet.',
  };
};

const findColumnsByAnswer = (
  answer: string,
  columns: WorksheetColumn[],
  count: number,
  excludeIds: string[] = [],
) => {
  const filtered = columns.filter((column) => !excludeIds.includes(column.id));
  if (!filtered.length) return [] as WorksheetColumn[];

  const indexMatches = (answer.match(/\d+/g) || [])
    .map((token) => Number(token))
    .filter((value) => value >= 1 && value <= filtered.length)
    .map((value) => filtered[value - 1]);

  const normalizedAnswer = normalize(answer);
  const nameMatches = filtered.filter((column) => {
    const target = normalize(column.name);
    return target && (normalizedAnswer.includes(target) || target.includes(normalizedAnswer));
  });

  return unique([...indexMatches, ...nameMatches]).slice(0, count);
};

const parseNumber = (answer: string) => {
  const match = answer.match(/-?\d*\.?\d+/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
};

const defaultTailFromPrompt = (prompt: string): 'two-tailed' | 'greater' | 'less' | undefined => {
  const text = normalize(prompt);
  if (/(two tailed|two sided|different from|not equal)/.test(text)) return 'two-tailed';
  if (/(greater|higher|above|more than)/.test(text)) return 'greater';
  if (/(less|lower|below|fewer than)/.test(text)) return 'less';
  return undefined;
};

const defaultAlphaFromPrompt = (prompt: string) => {
  const match = prompt.match(/0\.0\d+|0\.\d+/);
  if (!match) return undefined;
  const value = Number(match[0]);
  return value > 0 && value < 1 ? value : undefined;
};

const defaultNullValueFromPrompt = (prompt: string) => {
  const againstMatch = prompt.match(/(?:against|equal to|mean of|mu0|null)\s*(-?\d*\.?\d+)/i);
  if (!againstMatch) return undefined;
  const value = Number(againstMatch[1]);
  return Number.isFinite(value) ? value : undefined;
};

const defaultCapabilityValueFromPrompt = (prompt: string, label: 'lsl' | 'usl') => {
  const regex = label === 'lsl'
    ? /(lsl|lower spec(?:ification)?(?: limit)?)\s*[:=]?\s*(-?\d*\.?\d+)/i
    : /(usl|upper spec(?:ification)?(?: limit)?)\s*[:=]?\s*(-?\d*\.?\d+)/i;
  const match = prompt.match(regex);
  if (!match) return undefined;
  const value = Number(match[2]);
  return Number.isFinite(value) ? value : undefined;
};

const defaultHorizonFromPrompt = (prompt: string) => {
  const match = prompt.match(/next\s+(\d+)\s+(?:period|periods|step|steps|point|points|month|months|day|days)/i);
  if (!match) return undefined;
  const value = Number(match[1]);
  return Number.isFinite(value) && value >= 1 ? value : undefined;
};

const extractPlanHintsForIntent = (intent: AssistantIntent, prompt: string, worksheet: ParsedWorksheet): Partial<PlanSlots> => {
  const numericMatches = findColumnsByAnswer(prompt, worksheet.numericColumns, 3);
  const anyMatches = findColumnsByAnswer(prompt, worksheet.columns, 3);
  const normalized = normalize(prompt);
  const hints: Partial<PlanSlots> = {};

  if (intent === 'descriptive') {
    hints.columnId = numericMatches[0]?.id;
  }

  if (intent === 'hypothesis') {
    if (/(two sample|2 sample|compare|between two|group 1|group 2| versus | vs )/.test(` ${normalized} `)) {
      hints.subtype = 'two-sample-t';
    } else if (/(z test|ztest|one sample z)/.test(normalized)) {
      hints.subtype = 'one-sample-z';
    } else if (/(one sample|t test|ttest)/.test(normalized)) {
      hints.subtype = 'one-sample-t';
    }
    hints.columnId = numericMatches[0]?.id;
    hints.secondColumnId = numericMatches[1]?.id;
    hints.alpha = defaultAlphaFromPrompt(prompt);
    hints.tail = defaultTailFromPrompt(prompt);
    hints.nullValue = defaultNullValueFromPrompt(prompt);
  }

  if (intent === 'regression') {
    hints.subtype = /polynomial/.test(normalized) ? 'polynomial' : /regression|fit line|linear/.test(normalized) ? 'linear' : undefined;
    hints.independentColumnId = numericMatches[0]?.id;
    hints.dependentColumnId = numericMatches[1]?.id;
    const degreeMatch = prompt.match(/degree\s*(\d+)/i);
    if (degreeMatch) hints.parameterA = Number(degreeMatch[1]);
  }

  if (intent === 'distribution') {
    if (/normal/.test(normalized)) hints.distributionType = 'normal';
    if (/(student t|t distribution)/.test(normalized)) hints.distributionType = 'student-t';
    if (/chi square/.test(normalized)) hints.distributionType = 'chi-square';
    if (/f distribution/.test(normalized)) hints.distributionType = 'f-distribution';
    if (/exponential/.test(normalized)) hints.distributionType = 'exponential';
    if (/uniform/.test(normalized)) hints.distributionType = 'uniform';
    if (/binomial/.test(normalized)) hints.distributionType = 'binomial';
    if (/poisson/.test(normalized)) hints.distributionType = 'poisson';
    const numberValues = unique((prompt.match(/-?\d*\.?\d+/g) || []).map(Number).filter((value) => Number.isFinite(value)));
    if (numberValues[0] !== undefined) hints.parameterA = numberValues[0];
    if (numberValues[1] !== undefined) hints.parameterB = numberValues[1];
    if (numberValues[2] !== undefined) hints.xValue = numberValues[2];
  }

  if (intent === 'graph') {
    if (/histogram/.test(normalized)) hints.graphType = 'histogram';
    else if (/scatter/.test(normalized)) hints.graphType = 'scatter';
    else if (/bubble/.test(normalized)) hints.graphType = 'bubble';
    else if (/pareto/.test(normalized)) hints.graphType = 'pareto';
    else if (/heatmap/.test(normalized)) hints.graphType = 'heatmap';
    else if (/boxplot/.test(normalized)) hints.graphType = 'boxplot';
    else if (/run chart/.test(normalized)) hints.graphType = 'run';
    hints.columnIds = numericMatches.map((column) => column.id);
    hints.columnId = anyMatches[0]?.id;
  }

  if (intent === 'capability') {
    hints.columnId = numericMatches[0]?.id;
    hints.lsl = defaultCapabilityValueFromPrompt(prompt, 'lsl');
    hints.usl = defaultCapabilityValueFromPrompt(prompt, 'usl');
  }

  if (intent === 'forecast') {
    hints.columnId = numericMatches[0]?.id;
    if (/moving average/.test(normalized)) hints.method = 'moving-average';
    if (/exponential/.test(normalized)) hints.method = 'exponential-smoothing';
    if (/trend/.test(normalized)) hints.method = 'trend';
    hints.horizon = defaultHorizonFromPrompt(prompt);
  }

  if (intent === 'msa') {
    hints.studyType = /type 1/.test(normalized) ? 'type1' : /crossed/.test(normalized) ? 'crossed' : undefined;
    hints.measurementColumnId = numericMatches[0]?.id;
    hints.partColumnId = anyMatches[0]?.id;
    hints.operatorColumnId = anyMatches[1]?.id;
    const reference = defaultNullValueFromPrompt(prompt);
    if (reference !== undefined) hints.referenceValue = reference;
  }

  if (intent === 'doe') {
    hints.factorCount = /3 factor|three factor/.test(normalized) ? 3 : /2 factor|two factor/.test(normalized) ? 2 : undefined;
    hints.columnIds = numericMatches.map((column) => column.id);
    hints.responseColumnId = numericMatches[2]?.id;
    if (/maximi/.test(normalized)) hints.goal = 'maximize';
    if (/minimi/.test(normalized)) hints.goal = 'minimize';
    if (/target/.test(normalized)) hints.goal = 'target';
  }

  return hints;
};

const mergePlanHints = (slots: PlanSlots, hints: Partial<PlanSlots>): PlanSlots => {
  const nextSlots: PlanSlots = { ...slots };

  (Object.keys(hints) as Array<keyof PlanSlots>).forEach((key) => {
    const value = hints[key];
    if (value === undefined) return;
    if (nextSlots[key] === undefined) {
      (nextSlots as any)[key] = value;
    }
  });

  return nextSlots;
};

const inferPlanFromPrompt = (prompt: string, worksheet: ParsedWorksheet): AssistantPlan | null => {
  const intent = detectIntent(prompt);
  if (!intent) return null;

  return {
    intent,
    originalPrompt: prompt,
    slots: mergePlanHints({}, extractPlanHintsForIntent(intent, prompt, worksheet)),
  };
};

const buildNextQuestion = (plan: AssistantPlan, worksheet: ParsedWorksheet): PendingQuestion | null => {
  const numericOptions = worksheet.numericColumns.map((column) => ({ label: column.name, value: column.id }));
  const allOptions = worksheet.columns.map((column) => ({ label: column.name, value: column.id }));

  if (!worksheet.rowCount) {
    return {
      prompt: 'Your worksheet is empty. Open the Data tab and import, open, or paste data first, then ask me again.',
      field: 'columnId',
      kind: 'choice',
      options: [],
    };
  }

  switch (plan.intent) {
    case 'descriptive':
      if (!plan.slots.columnId) {
        return {
          prompt: `Which numeric column should I summarize?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'columnId',
          kind: 'numeric-column',
          options: numericOptions,
        };
      }
      return null;

    case 'hypothesis':
      if (!plan.slots.subtype) {
        return {
          prompt: 'Which test do you want? Choose one: 1) one-sample t-test, 2) two-sample t-test, 3) one-sample Z-test.',
          field: 'subtype',
          kind: 'choice',
          options: [
            { label: 'one-sample t-test', value: 'one-sample-t' },
            { label: 'two-sample t-test', value: 'two-sample-t' },
            { label: 'one-sample z-test', value: 'one-sample-z' },
          ],
        };
      }
      if (plan.slots.subtype === 'two-sample-t') {
        if (!plan.slots.columnId || !plan.slots.secondColumnId) {
          return {
            prompt: `Which two numeric columns should I compare? Reply with two names or numbers like "1 and 2".\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
            field: 'columnIds',
            kind: 'numeric-columns',
            count: 2,
            options: numericOptions,
          };
        }
      } else {
        if (!plan.slots.columnId) {
          return {
            prompt: `Which numeric column should I test?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
            field: 'columnId',
            kind: 'numeric-column',
            options: numericOptions,
          };
        }
        if (plan.slots.nullValue === undefined) {
          return {
            prompt: 'What hypothesized mean should I test against? For example: 0, 10, or 72.5.',
            field: 'nullValue',
            kind: 'number',
          };
        }
      }
      if (!plan.slots.tail) {
        return {
          prompt: 'Should this be 1) two-tailed, 2) greater-than, or 3) less-than?',
          field: 'tail',
          kind: 'choice',
          options: [
            { label: 'two-tailed', value: 'two-tailed' },
            { label: 'greater-than', value: 'greater' },
            { label: 'less-than', value: 'less' },
          ],
        };
      }
      if (plan.slots.alpha === undefined) {
        return {
          prompt: 'What significance level should I use? Common choices are 0.05 or 0.01.',
          field: 'alpha',
          kind: 'number',
        };
      }
      return null;

    case 'regression':
      if (!plan.slots.dependentColumnId) {
        return {
          prompt: `Which numeric column is the dependent / response variable (Y)?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'dependentColumnId',
          kind: 'numeric-column',
          options: numericOptions,
        };
      }
      if (!plan.slots.independentColumnId) {
        return {
          prompt: `Which numeric column should I use as the predictor (X)?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'independentColumnId',
          kind: 'numeric-column',
          options: numericOptions,
          excludeIds: [plan.slots.dependentColumnId],
        };
      }
      if (plan.slots.subtype === 'polynomial' && plan.slots.parameterA === undefined) {
        return {
          prompt: 'What polynomial degree should I use? Typical choices are 2 or 3.',
          field: 'parameterA',
          kind: 'number',
        };
      }
      return null;

    case 'distribution':
      if (!plan.slots.distributionType) {
        return {
          prompt: 'Which distribution do you want? Choose one: normal, student-t, chi-square, F, exponential, uniform, binomial, or poisson.',
          field: 'distributionType',
          kind: 'choice',
          options: [
            { label: 'normal', value: 'normal' },
            { label: 'student-t', value: 'student-t' },
            { label: 'chi-square', value: 'chi-square' },
            { label: 'F distribution', value: 'f-distribution' },
            { label: 'exponential', value: 'exponential' },
            { label: 'uniform', value: 'uniform' },
            { label: 'binomial', value: 'binomial' },
            { label: 'poisson', value: 'poisson' },
          ],
        };
      }
      if (plan.slots.parameterA === undefined) {
        return {
          prompt: 'Give me the first parameter. For example: mean μ, degrees of freedom, λ, or n depending on the distribution.',
          field: 'parameterA',
          kind: 'number',
        };
      }
      if (['normal', 'uniform', 'binomial', 'f-distribution'].includes(plan.slots.distributionType) && plan.slots.parameterB === undefined) {
        return {
          prompt: 'Give me the second parameter for this distribution.',
          field: 'parameterB',
          kind: 'number',
        };
      }
      if (plan.slots.xValue === undefined) {
        return {
          prompt: 'What x value should I evaluate the distribution at?',
          field: 'xValue',
          kind: 'number',
        };
      }
      return null;

    case 'graph':
      if (!plan.slots.graphType) {
        return {
          prompt: 'Which chart do you want? Options: histogram, scatter, bubble, run, pareto, heatmap, or boxplot.',
          field: 'graphType',
          kind: 'choice',
          options: [
            { label: 'histogram', value: 'histogram' },
            { label: 'scatter', value: 'scatter' },
            { label: 'bubble', value: 'bubble' },
            { label: 'run', value: 'run' },
            { label: 'pareto', value: 'pareto' },
            { label: 'heatmap', value: 'heatmap' },
            { label: 'boxplot', value: 'boxplot' },
          ],
        };
      }
      if (['histogram', 'run', 'boxplot'].includes(plan.slots.graphType) && !plan.slots.columnIds?.[0]) {
        return {
          prompt: `Which numeric column should I graph?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'columnIds',
          kind: 'numeric-columns',
          count: 1,
          options: numericOptions,
        };
      }
      if (['scatter', 'bubble'].includes(plan.slots.graphType) && (!plan.slots.columnIds || plan.slots.columnIds.length < 2)) {
        return {
          prompt: `Which two numeric columns should I plot? Reply with two names or numbers like "1 and 2".\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'columnIds',
          kind: 'numeric-columns',
          count: 2,
          options: numericOptions,
        };
      }
      if (plan.slots.graphType === 'heatmap' && (!plan.slots.columnIds || plan.slots.columnIds.length < 3)) {
        return {
          prompt: `Which numeric columns should I include in the correlation heatmap? Reply with three names or numbers if possible.\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'columnIds',
          kind: 'numeric-columns',
          count: Math.min(3, worksheet.numericColumns.length),
          options: numericOptions,
        };
      }
      if (plan.slots.graphType === 'pareto' && !plan.slots.columnId) {
        return {
          prompt: `Which categorical column should I use for the Pareto chart?\nAvailable columns: ${buildWorksheetContext(worksheet).allList}`,
          field: 'columnId',
          kind: 'any-column',
          options: allOptions,
        };
      }
      return null;

    case 'capability':
      if (!plan.slots.columnId) {
        return {
          prompt: `Which numeric measurement column should I use for capability analysis?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'columnId',
          kind: 'numeric-column',
          options: numericOptions,
        };
      }
      if (plan.slots.lsl === undefined) {
        return {
          prompt: 'What lower specification limit (LSL) should I use?',
          field: 'lsl',
          kind: 'number',
        };
      }
      if (plan.slots.usl === undefined) {
        return {
          prompt: 'What upper specification limit (USL) should I use?',
          field: 'usl',
          kind: 'number',
        };
      }
      return null;

    case 'forecast':
      if (!plan.slots.columnId) {
        return {
          prompt: `Which numeric series column should I forecast?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'columnId',
          kind: 'numeric-column',
          options: numericOptions,
        };
      }
      if (!plan.slots.method) {
        return {
          prompt: 'Which forecasting method do you want? Choose moving-average, exponential-smoothing, or trend.',
          field: 'method',
          kind: 'choice',
          options: [
            { label: 'moving-average', value: 'moving-average' },
            { label: 'exponential-smoothing', value: 'exponential-smoothing' },
            { label: 'trend', value: 'trend' },
          ],
        };
      }
      if (plan.slots.method === 'moving-average' && plan.slots.windowSize === undefined) {
        return {
          prompt: 'What moving-average window size should I use?',
          field: 'windowSize',
          kind: 'number',
        };
      }
      if (plan.slots.method === 'exponential-smoothing' && plan.slots.smoothingAlpha === undefined) {
        return {
          prompt: 'What smoothing alpha should I use? A common starting value is 0.35.',
          field: 'smoothingAlpha',
          kind: 'number',
        };
      }
      if (plan.slots.horizon === undefined) {
        return {
          prompt: 'How many future periods should I forecast?',
          field: 'horizon',
          kind: 'number',
        };
      }
      return null;

    case 'msa':
      if (!plan.slots.studyType) {
        return {
          prompt: 'Do you want a Type 1 gage study or a crossed study?',
          field: 'studyType',
          kind: 'choice',
          options: [
            { label: 'Type 1 gage study', value: 'type1' },
            { label: 'Crossed study', value: 'crossed' },
          ],
        };
      }
      if (!plan.slots.measurementColumnId) {
        return {
          prompt: `Which numeric measurement column should I use?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'measurementColumnId',
          kind: 'numeric-column',
          options: numericOptions,
        };
      }
      if (plan.slots.studyType === 'type1' && plan.slots.referenceValue === undefined) {
        return {
          prompt: 'What reference value should I compare the gage readings against?',
          field: 'referenceValue',
          kind: 'number',
        };
      }
      if (plan.slots.studyType === 'crossed' && !plan.slots.partColumnId) {
        return {
          prompt: `Which column identifies the part?\nAvailable columns: ${buildWorksheetContext(worksheet).allList}`,
          field: 'partColumnId',
          kind: 'any-column',
          options: allOptions,
        };
      }
      if (plan.slots.studyType === 'crossed' && !plan.slots.operatorColumnId) {
        return {
          prompt: `Which column identifies the operator or appraiser?\nAvailable columns: ${buildWorksheetContext(worksheet).allList}`,
          field: 'operatorColumnId',
          kind: 'any-column',
          options: allOptions,
          excludeIds: [plan.slots.partColumnId || ''],
        };
      }
      return null;

    case 'doe':
      if (!plan.slots.factorCount) {
        return {
          prompt: 'Should I set this up as a 2-factor or 3-factor DOE?',
          field: 'factorCount',
          kind: 'choice',
          options: [
            { label: '2-factor', value: '2' },
            { label: '3-factor', value: '3' },
          ],
        };
      }
      if (!plan.slots.columnIds || plan.slots.columnIds.length < plan.slots.factorCount) {
        return {
          prompt: `Which numeric columns are the factors? Reply with ${plan.slots.factorCount} names or numbers.\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'factorColumns',
          kind: 'numeric-columns',
          count: plan.slots.factorCount,
          options: numericOptions,
        };
      }
      if (!plan.slots.responseColumnId) {
        return {
          prompt: `Which numeric column is the response?\nAvailable numeric columns: ${buildWorksheetContext(worksheet).numericList}`,
          field: 'responseColumnId',
          kind: 'numeric-column',
          options: numericOptions,
          excludeIds: plan.slots.columnIds,
        };
      }
      if (!plan.slots.goal) {
        return {
          prompt: 'What is the optimization goal: maximize, minimize, or target?',
          field: 'goal',
          kind: 'choice',
          options: [
            { label: 'maximize', value: 'maximize' },
            { label: 'minimize', value: 'minimize' },
            { label: 'target', value: 'target' },
          ],
        };
      }
      if (plan.slots.goal === 'target' && plan.slots.targetValue === undefined) {
        return {
          prompt: 'What target response value should I use?',
          field: 'targetValue',
          kind: 'number',
        };
      }
      return null;

    default:
      return null;
  }
};

const applyAnswerToPlan = (
  plan: AssistantPlan,
  question: PendingQuestion,
  answer: string,
  worksheet: ParsedWorksheet,
): { nextPlan?: AssistantPlan; error?: string } => {
  const nextPlan: AssistantPlan = {
    ...plan,
    slots: { ...plan.slots },
  };

  if (question.kind === 'number') {
    const value = parseNumber(answer);
    if (value === null) {
      return { error: 'I could not read a number from that answer. Please reply with a numeric value.' };
    }
    (nextPlan.slots as any)[question.field] = value;
    return { nextPlan };
  }

  if (question.kind === 'choice') {
    const options = question.options || [];
    const normalizedAnswer = normalize(answer);
    const numericChoice = answer.match(/\d+/)?.[0];
    let selected = numericChoice ? options[Number(numericChoice) - 1] : undefined;
    if (!selected) {
      selected = options.find((option) => normalizedAnswer.includes(normalize(option.label)) || normalizedAnswer === normalize(option.value));
    }
    if (!selected) {
      return { error: `I could not match that answer to one of the choices. Please reply using one of these: ${options.map((option) => option.label).join(', ')}.` };
    }
    if (question.field === 'factorCount') {
      nextPlan.slots.factorCount = Number(selected.value) as 2 | 3;
    } else {
      (nextPlan.slots as any)[question.field] = selected.value;
    }
    return { nextPlan };
  }

  if (question.kind === 'numeric-column' || question.kind === 'any-column') {
    const columns = question.kind === 'numeric-column' ? worksheet.numericColumns : worksheet.columns;
    const matches = findColumnsByAnswer(answer, columns, 1, question.excludeIds || []);
    if (!matches[0]) {
      return { error: 'I could not match that to a worksheet column. Reply with the column name or its number from the list.' };
    }
    (nextPlan.slots as any)[question.field] = matches[0].id;
    return { nextPlan };
  }

  if (question.kind === 'numeric-columns') {
    const matches = findColumnsByAnswer(answer, worksheet.numericColumns, question.count || 1, question.excludeIds || []);
    if ((question.count || 1) > 1 && matches.length < (question.count || 1)) {
      return { error: `I need ${question.count} numeric columns for that step. Please reply with ${question.count} names or numbers.` };
    }
    if (question.field === 'factorColumns') {
      nextPlan.slots.columnIds = matches.map((column) => column.id);
      return { nextPlan };
    }
    if ((question.count || 1) === 1) {
      nextPlan.slots.columnIds = [matches[0].id];
    } else {
      nextPlan.slots.columnIds = matches.map((column) => column.id);
      nextPlan.slots.columnId = matches[0]?.id;
      nextPlan.slots.secondColumnId = matches[1]?.id;
    }
    return { nextPlan };
  }

  return { error: 'I could not process that answer.' };
};

const getColumn = (worksheet: ParsedWorksheet, columnId?: string | null) => worksheet.columns.find((column) => column.id === columnId);
const getNumericColumn = (worksheet: ParsedWorksheet, columnId?: string | null) => worksheet.numericColumns.find((column) => column.id === columnId);

const runPlan = (plan: AssistantPlan, worksheet: ParsedWorksheet): { reply: string; route: AssistantRoute } => {
  switch (plan.intent) {
    case 'descriptive': {
      const column = getNumericColumn(worksheet, plan.slots.columnId)!;
      const values = column.numericValues;
      const summary = [
        `I summarized **${column.name}** and opened **Descriptive Statistics**.`,
        `- n = ${values.length}`,
        `- mean = ${formatNumber(ss.mean(values))}`,
        `- median = ${formatNumber(ss.median(values))}`,
        `- sd = ${formatNumber(ss.sampleStandardDeviation(values))}`,
        `- min / max = ${formatNumber(Math.min(...values))} / ${formatNumber(Math.max(...values))}`,
      ].join('\n');
      return {
        reply: summary,
        route: { moduleId: 'descriptive', selectedColumnId: column.id },
      };
    }

    case 'hypothesis': {
      const alpha = plan.slots.alpha ?? 0.05;
      const tail = plan.slots.tail ?? 'two-tailed';
      if (plan.slots.subtype === 'two-sample-t') {
        const columnA = getNumericColumn(worksheet, plan.slots.columnId)!;
        const columnB = getNumericColumn(worksheet, plan.slots.secondColumnId)!;
        const dataA = columnA.numericValues;
        const dataB = columnB.numericValues;
        const mean1 = ss.mean(dataA);
        const mean2 = ss.mean(dataB);
        const var1 = ss.variance(dataA);
        const var2 = ss.variance(dataB);
        const pooledVar = ((dataA.length - 1) * var1 + (dataB.length - 1) * var2) / (dataA.length + dataB.length - 2);
        const standardError = Math.sqrt(pooledVar * (1 / dataA.length + 1 / dataB.length));
        const tStatistic = (mean1 - mean2) / standardError;
        const df = dataA.length + dataB.length - 2;
        let pValue = 0;
        if (tail === 'two-tailed') pValue = 2 * (1 - jStat.studentt.cdf(Math.abs(tStatistic), df));
        if (tail === 'greater') pValue = 1 - jStat.studentt.cdf(tStatistic, df);
        if (tail === 'less') pValue = jStat.studentt.cdf(tStatistic, df);
        return {
          reply: [
            `I ran a **two-sample t-test** comparing **${columnA.name}** vs **${columnB.name}** and opened **Hypothesis Testing**.`,
            `- mean(${columnA.name}) = ${formatNumber(mean1)}`,
            `- mean(${columnB.name}) = ${formatNumber(mean2)}`,
            `- t = ${formatNumber(tStatistic)}`,
            `- df = ${df}`,
            `- p-value = ${formatNumber(pValue)}`,
            `- decision at α = ${alpha}: ${pValue < alpha ? 'reject H₀' : 'fail to reject H₀'}`,
          ].join('\n'),
          route: { moduleId: 'hypothesis', selectedColumnId: columnA.id },
        };
      }

      const column = getNumericColumn(worksheet, plan.slots.columnId)!;
      const data = column.numericValues;
      const mu0 = plan.slots.nullValue ?? 0;
      const mean = ss.mean(data);
      const sd = ss.sampleStandardDeviation(data);
      const standardError = sd / Math.sqrt(data.length);
      const statistic = (mean - mu0) / standardError;
      const isZ = plan.slots.subtype === 'one-sample-z';
      const df = data.length - 1;
      let pValue = 0;
      if (isZ) {
        if (tail === 'two-tailed') pValue = 2 * (1 - jStat.normal.cdf(Math.abs(statistic), 0, 1));
        if (tail === 'greater') pValue = 1 - jStat.normal.cdf(statistic, 0, 1);
        if (tail === 'less') pValue = jStat.normal.cdf(statistic, 0, 1);
      } else {
        if (tail === 'two-tailed') pValue = 2 * (1 - jStat.studentt.cdf(Math.abs(statistic), df));
        if (tail === 'greater') pValue = 1 - jStat.studentt.cdf(statistic, df);
        if (tail === 'less') pValue = jStat.studentt.cdf(statistic, df);
      }
      return {
        reply: [
          `I ran a **${isZ ? 'one-sample Z-test' : 'one-sample t-test'}** on **${column.name}** against μ₀ = ${mu0} and opened **Hypothesis Testing**.`,
          `- sample mean = ${formatNumber(mean)}`,
          `- ${isZ ? 'z' : 't'} = ${formatNumber(statistic)}`,
          !isZ ? `- df = ${df}` : null,
          `- p-value = ${formatNumber(pValue)}`,
          `- decision at α = ${alpha}: ${pValue < alpha ? 'reject H₀' : 'fail to reject H₀'}`,
        ].filter(Boolean).join('\n'),
        route: { moduleId: 'hypothesis', selectedColumnId: column.id },
      };
    }

    case 'regression': {
      const xColumn = getNumericColumn(worksheet, plan.slots.independentColumnId)!;
      const yColumn = getNumericColumn(worksheet, plan.slots.dependentColumnId)!;
      const length = Math.min(xColumn.numericValues.length, yColumn.numericValues.length);
      const points = Array.from({ length }, (_, index) => [xColumn.numericValues[index], yColumn.numericValues[index]] as [number, number]);
      const line = ss.linearRegression(points);
      const rSquared = ss.rSquared(points, (x: number) => line.m * x + line.b);
      const subtype = plan.slots.subtype === 'polynomial' ? `polynomial setup requested (degree ${plan.slots.parameterA || 2})` : 'linear regression';
      return {
        reply: [
          `I prepared **${subtype}** using **${xColumn.name}** as X and **${yColumn.name}** as Y, and opened **Regression Analysis**.`,
          `- slope = ${formatNumber(line.m)}`,
          `- intercept = ${formatNumber(line.b)}`,
          `- R² = ${formatNumber(rSquared)}`,
          `- equation: y = ${formatNumber(line.m)}x + ${formatNumber(line.b)}`,
        ].join('\n'),
        route: { moduleId: 'regression', selectedColumnId: yColumn.id },
      };
    }

    case 'distribution': {
      const type = plan.slots.distributionType!;
      const a = plan.slots.parameterA!;
      const b = plan.slots.parameterB;
      const x = plan.slots.xValue!;
      let cdf = 0;
      let label = '';
      if (type === 'normal') {
        cdf = jStat.normal.cdf(x, a, b || 1);
        label = `Normal(μ=${a}, σ=${b || 1})`;
      } else if (type === 'student-t') {
        cdf = jStat.studentt.cdf(x, a);
        label = `t(df=${a})`;
      } else if (type === 'chi-square') {
        cdf = jStat.chisquare.cdf(x, a);
        label = `Chi-square(df=${a})`;
      } else if (type === 'f-distribution') {
        cdf = jStat.centralF.cdf(x, a, b || 1);
        label = `F(df1=${a}, df2=${b || 1})`;
      } else if (type === 'exponential') {
        cdf = jStat.exponential.cdf(x, a);
        label = `Exponential(λ=${a})`;
      } else if (type === 'uniform') {
        cdf = jStat.uniform.cdf(x, a, b || a + 1);
        label = `Uniform(a=${a}, b=${b || a + 1})`;
      } else if (type === 'binomial') {
        cdf = jStat.binomial.cdf(Math.round(x), Math.round(a), b || 0.5);
        label = `Binomial(n=${Math.round(a)}, p=${b || 0.5})`;
      } else {
        cdf = jStat.poisson.cdf(Math.round(x), a);
        label = `Poisson(λ=${a})`;
      }
      return {
        reply: [
          `I evaluated **${label}** at x = ${x} and opened **Probability Distributions**.`,
          `- P(X ≤ ${x}) = ${formatNumber(cdf)}`,
          `- P(X > ${x}) = ${formatNumber(1 - cdf)}`,
        ].join('\n'),
        route: { moduleId: 'distributions' },
      };
    }

    case 'graph': {
      const graphType = plan.slots.graphType!;
      const columnNames = (plan.slots.columnIds || []).map((columnId) => getColumn(worksheet, columnId)?.name).filter(Boolean);
      const singleColumn = getColumn(worksheet, plan.slots.columnId)?.name;
      const targetModule: AssistantModuleId = ['histogram', 'run', 'boxplot'].includes(graphType) ? 'visualization' : 'graph-builder';
      return {
        reply: [
          `I opened **${targetModule === 'graph-builder' ? 'Graph Builder' : 'Graphical Analysis'}** for a **${graphType}** workflow.`,
          columnNames.length ? `- numeric columns: ${columnNames.join(', ')}` : null,
          singleColumn ? `- categorical / support column: ${singleColumn}` : null,
        ].filter(Boolean).join('\n'),
        route: { moduleId: targetModule, selectedColumnId: plan.slots.columnIds?.[0] },
      };
    }

    case 'capability': {
      const column = getNumericColumn(worksheet, plan.slots.columnId)!;
      const values = column.numericValues;
      const lowerSpec = plan.slots.lsl!;
      const upperSpec = plan.slots.usl!;
      const mean = ss.mean(values);
      const sampleSigma = ss.sampleStandardDeviation(values);
      const cp = (upperSpec - lowerSpec) / (6 * sampleSigma);
      const cpu = (upperSpec - mean) / (3 * sampleSigma);
      const cpl = (mean - lowerSpec) / (3 * sampleSigma);
      const cpk = Math.min(cpu, cpl);
      return {
        reply: [
          `I analyzed **${column.name}** for capability and opened **Capability + Control Charts**.`,
          `- mean = ${formatNumber(mean)}`,
          `- Cp = ${formatNumber(cp)}`,
          `- Cpk = ${formatNumber(cpk)}`,
          `- specs = ${formatNumber(lowerSpec)} to ${formatNumber(upperSpec)}`,
        ].join('\n'),
        route: { moduleId: 'capability-control', selectedColumnId: column.id },
      };
    }

    case 'forecast': {
      const column = getNumericColumn(worksheet, plan.slots.columnId)!;
      const values = column.numericValues;
      const method = plan.slots.method || 'exponential-smoothing';
      const horizon = Math.max(1, Math.round(plan.slots.horizon || 6));
      let forecasts: number[] = [];
      if (method === 'moving-average') {
        const size = Math.max(2, Math.min(values.length - 1, Math.round(plan.slots.windowSize || 3)));
        const mean = ss.mean(values.slice(-size));
        forecasts = Array.from({ length: horizon }, () => mean);
      } else if (method === 'exponential-smoothing') {
        const alpha = Math.max(0.01, Math.min(0.99, plan.slots.smoothingAlpha || 0.35));
        let smoothed = values[0];
        for (let index = 1; index < values.length; index += 1) {
          smoothed = alpha * values[index - 1] + (1 - alpha) * smoothed;
        }
        forecasts = Array.from({ length: horizon }, () => smoothed);
      } else {
        const line = ss.linearRegression(values.map((value, index) => [index + 1, value] as [number, number]));
        forecasts = Array.from({ length: horizon }, (_, index) => line.m * (values.length + index + 1) + line.b);
      }
      return {
        reply: [
          `I forecast **${column.name}** with **${method}** and opened **Time Series + Forecasting**.`,
          `- forecast horizon = ${horizon}`,
          `- next values = ${forecasts.map((value) => formatNumber(value, 3)).join(', ')}`,
        ].join('\n'),
        route: { moduleId: 'time-series', selectedColumnId: column.id },
      };
    }

    case 'msa': {
      const column = getNumericColumn(worksheet, plan.slots.measurementColumnId)!;
      if (plan.slots.studyType === 'type1') {
        const reference = plan.slots.referenceValue ?? 0;
        const bias = ss.mean(column.numericValues) - reference;
        const sigma = ss.sampleStandardDeviation(column.numericValues);
        const cg = sigma === 0 ? Infinity : Math.abs(reference) / (6 * sigma);
        return {
          reply: [
            `I prepared a **Type 1 gage study** for **${column.name}** and opened **Measurement Systems Analysis**.`,
            `- reference value = ${formatNumber(reference)}`,
            `- bias = ${formatNumber(bias)}`,
            `- sample sd = ${formatNumber(sigma)}`,
            `- rough Cg = ${Number.isFinite(cg) ? formatNumber(cg) : '∞'}`,
          ].join('\n'),
          route: { moduleId: 'measurement-systems', selectedColumnId: column.id },
        };
      }

      const partColumn = getColumn(worksheet, plan.slots.partColumnId)!;
      const operatorColumn = getColumn(worksheet, plan.slots.operatorColumnId)!;
      return {
        reply: [
          `I prepared a **crossed MSA screening** and opened **Measurement Systems Analysis**.`,
          `- measurement column = ${column.name}`,
          `- part column = ${partColumn.name}`,
          `- operator column = ${operatorColumn.name}`,
        ].join('\n'),
        route: { moduleId: 'measurement-systems', selectedColumnId: column.id },
      };
    }

    case 'doe': {
      const factorColumns = (plan.slots.columnIds || []).map((columnId) => getNumericColumn(worksheet, columnId)).filter(Boolean) as NonNullable<ReturnType<typeof getNumericColumn>>[];
      const responseColumn = getNumericColumn(worksheet, plan.slots.responseColumnId)!;
      return {
        reply: [
          `I set up a **${plan.slots.factorCount}-factor DOE** path and opened **DOE + Response Optimizer**.`,
          `- factors = ${factorColumns.map((column) => column.name).join(', ')}`,
          `- response = ${responseColumn.name}`,
          `- optimization goal = ${plan.slots.goal}${plan.slots.goal === 'target' ? ` (${formatNumber(plan.slots.targetValue || 0)})` : ''}`,
        ].join('\n'),
        route: { moduleId: 'doe-optimizer', selectedColumnId: responseColumn.id },
      };
    }

    default:
      return {
        reply: 'I could not complete that analysis plan yet.',
        route: { moduleId: 'home' },
      };
  }
};

export const MapleStatsAiAssistant: React.FC<MapleStatsAiAssistantProps> = ({ worksheet, onRoute }) => {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [plan, setPlan] = useState<AssistantPlan | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<PendingQuestion | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const dragMovedRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const models = useSupabaseStore((state) => state.models);
  const fetchModels = useSupabaseStore((state) => state.fetchModels);
  const { settings } = useUserSettings('mapleos-ai', { defaultModelId: 'maplept-free' });

  const worksheetContext = useMemo(() => buildWorksheetContext(worksheet), [worksheet]);
  const activeModels = useMemo(() => (Array.isArray(models) ? models.filter((model) => model.is_active !== false) : []), [models]);
  const selectedModel = useMemo<AIModel | null>(() => {
    const preferredId = typeof settings?.defaultModelId === 'string' ? settings.defaultModelId : '';
    return activeModels.find((model) => model.id === preferredId) || activeModels[0] || null;
  }, [activeModels, settings?.defaultModelId]);
  const usingFrontierModel = isFrontierModel(selectedModel);

  useEffect(() => {
    if (!models?.length) {
      fetchModels().catch(() => undefined);
    }
  }, [fetchModels, models]);

  const buildModelMessages = useCallback((userPrompt: string, plannerReply?: string): Message[] => {
    const systemContent = plannerReply
      ? [
          'You are MapleStats AI inside MapleOS.',
          'The worksheet planner has already determined the correct next step.',
          'Rewrite the planner response so it feels natural and concise.',
          'Preserve every concrete setup detail exactly as provided.',
          'Do not invent metrics, test results, columns, or actions that are not present.',
        ].join(' ')
      : [
          'You are MapleStats AI inside MapleOS.',
          'Answer the user using only the worksheet context provided.',
          'Be concise, practical, and statistical.',
          'If the worksheet is missing what is needed, say exactly what data or columns are needed next.',
          'Do not invent results or claim an analysis ran if it did not.',
        ].join(' ');

    const context = [
      worksheetContext.summary,
      `Numeric columns: ${worksheetContext.numericList}`,
      `All columns: ${worksheetContext.allList}`,
      plannerReply ? `Planner reply: ${plannerReply}` : '',
      `User prompt: ${userPrompt}`,
    ]
      .filter(Boolean)
      .join('\n');

    return [
      {
        id: createId(),
        chatId: 'maplestats-ai',
        role: 'system',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        content: systemContent,
      } as Message,
      {
        id: createId(),
        chatId: 'maplestats-ai',
        role: 'user',
        sender: 'user',
        timestamp: new Date().toISOString(),
        content: context,
      } as Message,
    ];
  }, [worksheetContext.allList, worksheetContext.numericList, worksheetContext.summary]);

  const getFrontierReply = useCallback(async (userPrompt: string, plannerReply?: string) => {
    if (!selectedModel || !usingFrontierModel || activeModels.length === 0) {
      return plannerReply || null;
    }

    setIsThinking(true);
    try {
      const response = await sendMessageToModel(buildModelMessages(userPrompt, plannerReply), selectedModel, activeModels, {
        temperature: 0.2,
      });
      return response.content?.trim() || plannerReply || null;
    } catch (error) {
      console.error('MapleStats AI frontier request failed:', error);
      return plannerReply || null;
    } finally {
      setIsThinking(false);
    }
  }, [activeModels, buildModelMessages, selectedModel, usingFrontierModel]);

  useEffect(() => {
    setMessages((current) => {
      if (current.length > 0) return current;
      return [
        {
          id: createId(),
          role: 'assistant',
          content: `${worksheetContext.summary}\n\nTry prompts like:\n- do a t-test on this data\n- summarize the temperature column\n- run regression on pressure vs temperature\n- forecast the runtime series\n- check capability for humidity`,
        },
      ];
    });
  }, [worksheetContext.summary]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging || !dragStartRef.current || !initialOffsetRef.current) return;
      const dx = event.clientX - dragStartRef.current.x;
      const dy = event.clientY - dragStartRef.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        dragMovedRef.current = true;
      }
      setOffset({
        x: initialOffsetRef.current.x + dx,
        y: initialOffsetRef.current.y + dy,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      window.setTimeout(() => {
        dragMovedRef.current = false;
      }, 0);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (isMinimized || isHidden) return;

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    scrollToBottom();
    const timer = window.setTimeout(scrollToBottom, 0);
    return () => window.clearTimeout(timer);
  }, [isHidden, isMinimized, messages]);

  const appendMessage = (role: 'assistant' | 'user', content: string) => {
    setMessages((current) => [...current, { id: createId(), role, content }]);
  };

  const advancePlan = async (currentPlan: AssistantPlan, sourcePrompt: string) => {
    const nextQuestion = buildNextQuestion(currentPlan, worksheet);
    if (nextQuestion?.options?.length === 0 && nextQuestion.kind === 'choice') {
      appendMessage('assistant', nextQuestion.prompt);
      setPlan(null);
      setPendingQuestion(null);
      onRoute({ moduleId: 'home', workspaceTab: 'data' });
      return;
    }

    if (nextQuestion) {
      setPlan(currentPlan);
      setPendingQuestion(nextQuestion);
      appendMessage('assistant', nextQuestion.prompt);
      return;
    }

    const { reply, route } = runPlan(currentPlan, worksheet);
    const finalReply = (await getFrontierReply(sourcePrompt, reply)) || reply;
    appendMessage('assistant', finalReply);
    onRoute(route);
    setPlan(null);
    setPendingQuestion(null);
  };

  const handleSend = async () => {
    const nextPrompt = prompt.trim();
    if (!nextPrompt || isThinking) return;

    appendMessage('user', nextPrompt);
    setPrompt('');
    setIsHidden(false);
    setIsMinimized(false);

    if (/^(cancel|start over|reset)$/i.test(nextPrompt)) {
      setPlan(null);
      setPendingQuestion(null);
      appendMessage('assistant', 'Okay — I reset the current analysis flow. Ask for a new analysis whenever you\'re ready.');
      return;
    }

    if (pendingQuestion && plan) {
      const { nextPlan, error } = applyAnswerToPlan(plan, pendingQuestion, nextPrompt, worksheet);
      if (error || !nextPlan) {
        appendMessage('assistant', error || 'I could not use that answer. Please try again.');
        appendMessage('assistant', pendingQuestion.prompt);
        return;
      }
      const enrichedPlan: AssistantPlan = {
        ...nextPlan,
        slots: mergePlanHints(nextPlan.slots, extractPlanHintsForIntent(nextPlan.intent, nextPrompt, worksheet)),
      };
      await advancePlan(enrichedPlan, nextPrompt);
      return;
    }

    const inferredPlan = inferPlanFromPrompt(nextPrompt, worksheet);
    if (!inferredPlan) {
      if (usingFrontierModel) {
        const frontierReply = await getFrontierReply(nextPrompt);
        appendMessage(
          'assistant',
          frontierReply || 'I could not answer that from the worksheet context. Try asking about a column, an analysis, or what data is needed next.',
        );
        return;
      }

      appendMessage(
        'assistant',
        'I can help with descriptive statistics, t-tests, regression, distributions, graphing, capability, forecasting, MSA, and DOE. For example: "do a t-test on this data", "forecast the runtime column", or "check capability with LSL 10 and USL 14".',
      );
      return;
    }

    await advancePlan(inferredPlan, nextPrompt);
  };

  if (isHidden) {
    return (
      <div
        className="absolute bottom-4 right-4 z-50"
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      >
        <Button
          variant="secondary"
          className="rounded-full shadow-lg cursor-grab active:cursor-grabbing"
          onMouseDown={(event) => {
            dragMovedRef.current = false;
            setIsDragging(true);
            dragStartRef.current = { x: event.clientX, y: event.clientY };
            initialOffsetRef.current = { ...offset };
            event.preventDefault();
          }}
          onClick={() => {
            if (dragMovedRef.current) return;
            setIsHidden(false);
            setIsMinimized(false);
          }}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Show MapleStats AI
        </Button>
      </div>
    );
  }

  return (
    <div
      className="absolute bottom-4 right-4 z-50 w-[26rem] max-w-[calc(100%-2rem)]"
      style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
    >
      <div className="rounded-2xl border border-border bg-card/95 shadow-2xl backdrop-blur">
        <div
          className="flex items-center justify-between gap-3 border-b border-border px-3 py-2"
          onMouseDown={(event) => {
            setIsDragging(true);
            dragStartRef.current = { x: event.clientX, y: event.clientY };
            initialOffsetRef.current = { ...offset };
            event.preventDefault();
          }}
        >
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary p-2 text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">MapleStats AI</div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Guided worksheet analysis assistant</span>
                <Badge variant="secondary" className="h-5 px-2 text-[10px] font-medium">
                  {usingFrontierModel && selectedModel ? `${selectedModel.name} · AI Credits` : 'Built-in planner'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div onMouseDown={(event) => event.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsMinimized((current) => !current)}
                aria-label={isMinimized ? 'Expand MapleStats AI' : 'Minimize MapleStats AI'}
              >
                {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
            <div onMouseDown={(event) => event.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsHidden(true)}
                aria-label="Hide MapleStats AI"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 cursor-grab active:cursor-grabbing"
              onMouseDown={(event) => {
                event.stopPropagation();
                setIsDragging(true);
                dragStartRef.current = { x: event.clientX, y: event.clientY };
                initialOffsetRef.current = { ...offset };
                event.preventDefault();
              }}
              aria-label="Move MapleStats AI"
            >
              <GripHorizontal className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>

        </div>

        {!isMinimized ? (
          <>
            <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
              {worksheetContext.summary}
            </div>

            <ScrollArea className="h-72 px-3 py-3">
              <div className="space-y-3 pr-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                      message.role === 'assistant'
                        ? 'bg-muted text-foreground'
                        : 'ml-8 bg-primary text-primary-foreground'
                    }`}
                  >
                    {message.content}
                  </div>
                ))}
                <div ref={messagesEndRef} className="h-0" />
              </div>
            </ScrollArea>

            <div className="border-t border-border p-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void handleSend();
                      }
                    }}
                    placeholder="Ask MapleStats AI what analysis to run…"
                    disabled={isThinking}
                  />
                  <Button size="icon" onClick={() => void handleSend()} disabled={!prompt.trim() || isThinking}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {isThinking
                    ? `Thinking with ${selectedModel?.name || 'the selected model'}…`
                    : usingFrontierModel
                      ? 'Using your selected frontier model through MapleOS AI Credits when MapleStats needs model help.'
                      : 'Ask about t-tests, regression, forecasting, graphs, capability, MSA, or DOE. It always asks follow-up questions first when the analysis setup is ambiguous.'}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            Assistant minimized. Use the chevron to reopen or the X button to hide it.
          </div>
        )}
      </div>
    </div>
  );
};